// ======================================
// Services Pages Module (Dynamic API)
// ======================================

import { icons, renderServiceCard } from '../components.js';
import { servicesAPI } from '../lib/api.js';
import { designStyles } from '../data.js';

// Services Overview Page
export function renderServicesPage() {
  return `
    <section class="page-header">
      <div class="container">
        <div class="page-header-content">
          <h1 class="page-title">Our Interior Services</h1>
          <p class="page-subtitle">
            From modular kitchens to complete home makeovers, we offer comprehensive 
            interior design services tailored to your needs and budget.
          </p>
        </div>
      </div>
    </section>

    <section class="section services-section">
      <div class="container">
        <div class="services-grid" id="services-grid">
          <!-- Loading Placeholder -->
          <div class="loading-placeholder" style="grid-column:1/-1;text-align:center;padding:3rem;">
            Loading services...
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="section cta-section">
      <div class="container">
        <div class="cta-content">
          <h2 class="cta-title">Not Sure What You Need?</h2>
          <p class="cta-subtitle">
            Our design experts will help you figure out the best solutions for your home.
          </p>
          <button class="btn btn-lg cta-btn" data-open-modal="lead-modal">
            Get Free Consultation
          </button>
        </div>
      </div>
    </section>
  `;
}

export async function initServicesPage() {
  const grid = document.getElementById('services-grid');
  if (!grid) return;

  try {
    const response = await servicesAPI.list();
    const services = response.data || [];

    if (services.length > 0) {
      grid.innerHTML = services.map(service => renderServiceCard(service)).join('');
    } else {
      grid.innerHTML = '<div style="grid-column:1/-1;text-align:center;">No services available at the moment.</div>';
    }
  } catch (error) {
    grid.innerHTML = '<div class="error-message">Failed to load services. Please try again later.</div>';
  }
}

// Service Detail Page
export function renderServiceDetailPage(params) {
  return `
    <div id="service-detail-container">
        <section class="page-header">
            <div class="container">
                <div class="page-header-content">
                <h1 class="page-title">Loading...</h1>
                </div>
            </div>
        </section>
    </div>
  `;
}

export async function initServiceDetailPage(params) {
  const container = document.getElementById('service-detail-container');
  if (!container) return;

  try {
    // Fetch by SLUG
    const response = await servicesAPI.get(params.slug);
    const service = response.data;

    if (!service) {
      renderNotFound(container);
      return;
    }

    renderServiceContent(container, service);

  } catch (error) {
    console.error(error);
    renderNotFound(container);
  }
}

function renderNotFound(container) {
  container.innerHTML = `
      <section class="page-header">
        <div class="container">
          <div class="page-header-content">
            <h1 class="page-title">Service Not Found</h1>
            <p class="page-subtitle">The service you're looking for doesn't exist.</p>
            <a href="/services" class="btn btn-primary mt-6">View All Services</a>
          </div>
        </div>
      </section>
    `;
}

function renderServiceContent(container, service) {
  // Parse highlights if it's a string/JSON
  let highlights = [];
  if (Array.isArray(service.highlights)) {
    highlights = service.highlights;
  } else if (typeof service.highlights === 'string') {
    try { highlights = JSON.parse(service.highlights); } catch (e) { console.error('Error parsing highlights', e); }
  }

  container.innerHTML = `
    <section class="section service-hero">
      <div class="container">
        <div class="service-hero-grid">
          <div class="service-hero-content">
            <span class="service-hero-badge">
              ${icons.sparkles || '✨'}
              Premium Service
            </span>
            <h1 class="service-hero-title">${service.title}</h1>
            <p class="service-hero-desc">${service.full_description || service.description}</p>
            
            <div class="service-highlights">
              ${highlights.map(h => `
                <span class="service-highlight">
                  ${icons.check || '✓'}
                  ${h}
                </span>
              `).join('')}
            </div>
            
            <div class="hero-ctas">
              <button class="btn btn-primary btn-lg" data-open-modal="lead-modal">
                Get Free Quote
              </button>
              <a href="/estimator" class="btn btn-secondary btn-lg">
                Cost Estimator
              </a>
            </div>
          </div>
          
          <div class="service-hero-gallery">
            <img src="${service.image || 'https://via.placeholder.com/600x400'}" alt="${service.title}" class="service-hero-img">
            <img src="https://images.unsplash.com/photo-1600210492493-0946911123ea?w=300&h=200&fit=crop" alt="Design detail" class="service-hero-img">
            <img src="https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=300&h=200&fit=crop" alt="Material sample" class="service-hero-img">
          </div>
        </div>
      </div>
    </section>

    <!-- Design Styles Section -->
    <section class="section design-styles-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">Design Styles</h2>
          <p class="section-subtitle">
            Choose from a variety of design aesthetics that match your taste.
          </p>
        </div>
        <div class="design-styles-grid">
          ${designStyles.map(style => `
            <div class="design-style-card">
              <img src="${style.image}" alt="${style.name}" class="design-style-img">
              <div class="design-style-overlay">
                <span class="design-style-name">${style.name}</span>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    </section>

    <!-- Materials Section -->
    <section class="section materials-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">Premium Materials</h2>
          <p class="section-subtitle">
            We use only the best materials from trusted brands.
          </p>
        </div>
        <div class="materials-grid">
          <div class="material-card">
            <div class="material-icon">${icons.tool || '🛠️'}</div>
            <h3 class="material-title">BWP Plywood</h3>
            <p class="material-desc">Boiling water proof plywood from trusted brands for long-lasting durability.</p>
          </div>
          <div class="material-card">
            <div class="material-icon">${icons.shield || '🛡️'}</div>
            <h3 class="material-title">Premium Hardware</h3>
            <p class="material-desc">Soft-close hinges and channels from Hettich, Hafele, and Blum.</p>
          </div>
          <div class="material-card">
            <div class="material-icon">${icons.sparkles || '✨'}</div>
            <h3 class="material-title">Quality Finishes</h3>
            <p class="material-desc">Laminate, acrylic, PU, and membrane finishes in 100+ colors and textures.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section class="section pricing-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">Transparent Pricing</h2>
          <p class="section-subtitle">
            Starting from <strong>${service.price_range || 'Ask for Quote'}</strong> based on your requirements.
          </p>
        </div>
        <div class="pricing-table">
          <div class="pricing-header">
            <div class="pricing-header-cell">Feature</div>
            <div class="pricing-header-cell">Essential</div>
            <div class="pricing-header-cell">Premium</div>
            <div class="pricing-header-cell">Luxe</div>
          </div>
          <div class="pricing-row">
            <div class="pricing-cell">BWP Plywood</div>
            <div class="pricing-cell"><span class="check">${icons.check || '✓'}</span></div>
            <div class="pricing-cell"><span class="check">${icons.check || '✓'}</span></div>
            <div class="pricing-cell"><span class="check">${icons.check || '✓'}</span></div>
          </div>
          <div class="pricing-row">
            <div class="pricing-cell">Soft-close Hardware</div>
            <div class="pricing-cell"><span class="check">${icons.check || '✓'}</span></div>
            <div class="pricing-cell"><span class="check">${icons.check || '✓'}</span></div>
            <div class="pricing-cell"><span class="check">${icons.check || '✓'}</span></div>
          </div>
          <div class="pricing-row">
            <div class="pricing-cell">Premium Laminate</div>
            <div class="pricing-cell"><span class="x">${icons.x || '✗'}</span></div>
            <div class="pricing-cell"><span class="check">${icons.check || '✓'}</span></div>
            <div class="pricing-cell"><span class="check">${icons.check || '✓'}</span></div>
          </div>
          <div class="pricing-row">
            <div class="pricing-cell">Acrylic / PU Finish</div>
            <div class="pricing-cell"><span class="x">${icons.x || '✗'}</span></div>
            <div class="pricing-cell"><span class="x">${icons.x || '✗'}</span></div>
            <div class="pricing-cell"><span class="check">${icons.check || '✓'}</span></div>
          </div>
          <div class="pricing-row">
            <div class="pricing-cell">10-Year Warranty</div>
            <div class="pricing-cell"><span class="check">${icons.check || '✓'}</span></div>
            <div class="pricing-cell"><span class="check">${icons.check || '✓'}</span></div>
            <div class="pricing-cell"><span class="check">${icons.check || '✓'}</span></div>
          </div>
        </div>
        <div style="text-align: center; margin-top: var(--space-8);">
          <a href="/estimator" class="btn btn-primary btn-lg">Get Detailed Quote</a>
        </div>
      </div>
    </section>

    <!-- Timeline Section -->
    <section class="section timeline-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">Project Timeline</h2>
          <p class="section-subtitle">
            Typical delivery: <strong>${service.timeline || 'TBD'}</strong>
          </p>
        </div>
        <div class="timeline-container">
          <div class="timeline-item">
            <div class="timeline-marker">${icons.calendar || '📅'}</div>
            <div class="timeline-content">
              <h3 class="timeline-title">Design & Planning</h3>
              <p class="timeline-desc">Site visit, measurements, 3D design creation, and final approval.</p>
              <span class="timeline-duration">${icons.clock || '🕒'} 5-7 days</span>
            </div>
          </div>
          <div class="timeline-item">
            <div class="timeline-marker">${icons.tool || '🛠️'}</div>
            <div class="timeline-content">
              <h3 class="timeline-title">Manufacturing</h3>
              <p class="timeline-desc">Precision manufacturing at our factory with quality checks at every stage.</p>
              <span class="timeline-duration">${icons.clock || '🕒'} 15-20 days</span>
            </div>
          </div>
          <div class="timeline-item">
            <div class="timeline-marker">${icons.home || '🏠'}</div>
            <div class="timeline-content">
              <h3 class="timeline-title">Installation & Handover</h3>
              <p class="timeline-desc">Professional installation, final inspection, and handover with warranty docs.</p>
              <span class="timeline-duration">${icons.clock || '🕒'} 5-10 days</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="section cta-section">
      <div class="container">
        <div class="cta-content">
          <h2 class="cta-title">Ready for Your New ${service.title}?</h2>
          <p class="cta-subtitle">
            Book a free consultation and get a customized quote today.
          </p>
          <button class="btn btn-lg cta-btn" data-open-modal="lead-modal">
            Book Free Consultation
          </button>
        </div>
      </div>
    </section>
    `;

  // Re-initialize modal triggers for the new content
  // Assuming global initUI handles it, or we dispatch event
  window.dispatchEvent(new Event('content:updated'));
}
